import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// ============================================================
// 🔗 API BASE URL
// ============================================================

const BASE_URL =
  process.env.REACT_APP_BACKEND_BASE_URL ||
  "http://localhost:5000";

const api = axios.create({
  baseURL: `${BASE_URL}/doctors/availability`,
});

// ============================================================
// 🔐 TOKEN INTERCEPTOR
// ============================================================

api.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken");

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ============================================================
// 🧠 HELPERS
// ============================================================

const toNumber = (value, fallback = 0) => {
  const number = Number(value);

  return Number.isFinite(number)
    ? number
    : fallback;
};

const getId = (item) => {
  if (!item) return null;

  return (
    item.slotId ||
    item.roundId ||
    item.operationId ||
    item._id ||
    item.id ||
    null
  );
};

const getRoundId = (round) => {
  if (!round) return null;

  return (
    round.roundId ||
    round._id ||
    round.id ||
    null
  );
};

const getOperationId = (operation) => {
  if (!operation) return null;

  return (
    operation.operationId ||
    operation._id ||
    operation.id ||
    null
  );
};

const sameId = (a, b) => {
  if (!a || !b) return false;

  return String(a) === String(b);
};

const normalizeStatus = (slot) => {
  const raw = String(
    slot?.status ||
      slot?.slotStatus ||
      "available"
  )
    .trim()
    .toLowerCase();

  if (
    raw === "booked" ||
    raw === "confirmed"
  ) {
    return "booked";
  }

  if (
    raw === "blocked" ||
    raw === "block"
  ) {
    return "blocked";
  }

  return "available";
};

// ============================================================
// 📊 RECALCULATE COUNTS
// ============================================================

const calculateSlotCounts = (slots = []) => {
  const counts = {
    total: 0,
    booked: 0,
    available: 0,
    blocked: 0,
  };

  if (!Array.isArray(slots)) {
    return counts;
  }

  slots.forEach((slot) => {
    counts.total++;

    const status = normalizeStatus(slot);

    if (status === "booked") {
      counts.booked++;
    } else if (status === "blocked") {
      counts.blocked++;
    } else {
      counts.available++;
    }
  });

  return counts;
};

// ============================================================
// 📊 UPDATE STATE COUNTS
// ============================================================

const recalculateStateCounts = (state) => {
  const counts = calculateSlotCounts(
    state.allSlots
  );

  state.totalSlots = counts.total;
  state.bookedSlots = counts.booked;
  state.availableSlots = counts.available;
  state.blockedSlots = counts.blocked;
};

// ============================================================
// 🧠 NORMALIZE AVAILABILITY DATA
// ============================================================

const normalizeAvailabilityData = (
  rawData = {}
) => {
  const response = rawData || {};

  const data =
    response.data &&
    typeof response.data === "object" &&
    !Array.isArray(response.data)
      ? response.data
      : response;

  // ==========================================================
  // SESSIONS
  // ==========================================================

  const sessions = Array.isArray(
    data.sessions
  )
    ? data.sessions
    : [];

  // ==========================================================
  // FLATTEN SLOTS
  // ==========================================================

  const allSlots = sessions.flatMap(
    (session) => {
      if (
        !Array.isArray(
          session?.slots
        )
      ) {
        return [];
      }

      return session.slots.map(
        (slot) => ({
          ...slot,

          sessionName:
            session?.name ||
            session?.sessionName ||
            "",

          session:
            session?.name ||
            session?.sessionName ||
            "",

          sessionSlotDuration:
            session?.slotDuration ??
            null,

          sessionMaxPatients:
            session?.maxPatients ??
            0,
        })
      );
    }
  );

  // ==========================================================
  // DIRECT SLOTS FALLBACK
  // ==========================================================

  const directSlots =
    Array.isArray(data.slots)
      ? data.slots
      : [];

  const finalSlots =
    allSlots.length > 0
      ? allSlots
      : directSlots;

  // ==========================================================
  // DOCTOR ROUNDS
  // ==========================================================

  const doctorOnRounds =
    Array.isArray(data.rounds)
      ? data.rounds
      : Array.isArray(
          data.doctorOnRounds
        )
      ? data.doctorOnRounds
      : [];

  // ==========================================================
  // OPERATIONS
  // ==========================================================

  const operationSchedule =
    Array.isArray(
      data.operationSchedules
    )
      ? data.operationSchedules
      : Array.isArray(
          data.operationSchedule
        )
      ? data.operationSchedule
      : [];

  // ==========================================================
  // COUNTS
  // ==========================================================

  const calculated =
    calculateSlotCounts(
      finalSlots
    );

  // ==========================================================
  // SESSION NORMALIZATION
  // ==========================================================

  const normalizeSession = (
    session
  ) => {
    const slots =
      Array.isArray(
        session?.slots
      )
        ? session.slots
        : [];

    const counts =
      calculateSlotCounts(
        slots
      );

    return {
      ...session,

      name:
        session?.name ||
        session?.sessionName ||
        "",

      total:
        session?.total != null
          ? toNumber(
              session.total
            )
          : counts.total,

      booked:
        session?.booked != null
          ? toNumber(
              session.booked
            )
          : counts.booked,

      available:
        session?.available != null
          ? toNumber(
              session.available
            )
          : counts.available,

      blocked:
        session?.blocked != null
          ? toNumber(
              session.blocked
            )
          : counts.blocked,

      slots,
    };
  };

  const normalizedSessions =
    sessions.map(
      normalizeSession
    );

  // ==========================================================
  // SESSION HELPERS
  // ==========================================================

  const getSession = (names) => {
    const normalizedNames =
      names.map((name) =>
        String(name)
          .trim()
          .toLowerCase()
      );

    return (
      normalizedSessions.find(
        (session) =>
          normalizedNames.includes(
            String(
              session?.name || ""
            )
              .trim()
              .toLowerCase()
          )
      ) || {
        name: names[0] || "",
        total: 0,
        booked: 0,
        available: 0,
        blocked: 0,
        slots: [],
      }
    );
  };

  return {
    ...data,

    success:
      data.success ??
      response.success ??
      true,

    date:
      data.date ||
      response.date ||
      null,

    source:
      data.source ||
      response.source ||
      "",

    isPastDay:
      data.isPastDay ??
      response.isPastDay ??
      false,

    isToday:
      data.isToday ??
      response.isToday ??
      false,

    isHoliday:
      data.isHoliday ??
      response.isHoliday ??
      false,

    holidayReason:
      data.holidayReason ||
      response.holidayReason ||
      "",

    sessions:
      normalizedSessions,

    allSlots:
      finalSlots,

    slots:
      finalSlots,

    doctorOnRounds,

    rounds:
      doctorOnRounds,

    operationSchedule,

    operationSchedules:
      operationSchedule,

    totalSlots:
      data.totalSlots != null
        ? toNumber(
            data.totalSlots
          )
        : calculated.total,

    bookedSlots:
      data.bookedSlots != null
        ? toNumber(
            data.bookedSlots
          )
        : calculated.booked,

    availableSlots:
      data.availableSlots != null
        ? toNumber(
            data.availableSlots
          )
        : calculated.available,

    blockedSlots:
      data.blockedSlots != null
        ? toNumber(
            data.blockedSlots
          )
        : calculated.blocked,

    morning:
      getSession(["morning"]),

    afternoon:
      getSession(["afternoon"]),

    evening:
      getSession(["evening"]),

    night:
      getSession(["night"]),
  };
};

// ============================================================
// 🧹 RESET DATE STATE
// ============================================================

const resetDateState = (state) => {
  state.selectedDate = null;
  state.source = "";

  state.isPastDay = false;
  state.isToday = false;

  state.isHoliday = false;
  state.holidayReason = "";

  state.sessions = [];
  state.allSlots = [];

  state.doctorOnRounds = [];

  state.operationSchedule = [];
  state.operationSchedules = [];

  state.totalSlots = 0;
  state.bookedSlots = 0;
  state.availableSlots = 0;
  state.blockedSlots = 0;

  state.myAvailability = null;
};

// ============================================================
// ❌ ERROR MESSAGE HELPER
// ============================================================

const getErrorMessage = (
  err,
  fallback
) => {
  return (
    err.response?.data?.message ||
    err.response?.data?.error ||
    err.message ||
    fallback
  );
};

// ============================================================
// 📦 CREATE AVAILABILITY
// POST /
// ============================================================

export const createAvailability =
  createAsyncThunk(
    "doctorAvailability/create",

    async (
      payload,
      { rejectWithValue }
    ) => {
      try {
        const res =
          await api.post(
            "/",
            payload
          );

        return res.data;
      } catch (err) {
        return rejectWithValue(
          getErrorMessage(
            err,
            "Failed to create availability"
          )
        );
      }
    }
  );

// ============================================================
// ➕ ADD ENTIRE DAY
// POST /day
// ============================================================

export const addEntireDay =
  createAsyncThunk(
    "doctorAvailability/addEntireDay",

    async (
      payload,
      { rejectWithValue }
    ) => {
      try {
        if (!payload) {
          return rejectWithValue(
            "Day availability data is required"
          );
        }

        const res =
          await api.post(
            "/day",
            payload
          );

        return res.data;
      } catch (err) {
        return rejectWithValue(
          getErrorMessage(
            err,
            "Failed to add day"
          )
        );
      }
    }
  );

// ============================================================
// ➕ ADD SINGLE SLOT
// POST /slot
// ============================================================

export const addSingleSlot =
  createAsyncThunk(
    "doctorAvailability/addSingleSlot",

    async (
      payload,
      { rejectWithValue }
    ) => {
      try {
        if (!payload) {
          return rejectWithValue(
            "Slot data is required"
          );
        }

        const res =
          await api.post(
            "/slot",
            payload
          );

        return res.data;
      } catch (err) {
        return rejectWithValue(
          getErrorMessage(
            err,
            "Failed to add slot"
          )
        );
      }
    }
  );

// ============================================================
// 📋 COPY WEEKLY AVAILABILITY
// POST /copy-week
// ============================================================

export const copyWeeklyAvailability =
  createAsyncThunk(
    "doctorAvailability/copyWeeklyAvailability",

    async (
      payload,
      { rejectWithValue }
    ) => {
      try {
        if (!payload) {
          return rejectWithValue(
            "Copy weekly availability data is required"
          );
        }

        // ✅ CORRECT BACKEND ROUTE
        // POST /doctors/availability/copy-week
        const res =
          await api.post(
            "/copy-week",
            payload
          );

        return res.data;
      } catch (err) {
        return rejectWithValue(
          getErrorMessage(
            err,
            "Failed to copy weekly availability"
          )
        );
      }
    }
  );

// ============================================================
// ✏️ UPDATE FULL AVAILABILITY
// PUT /my
// ============================================================

export const updateAvailability =
  createAsyncThunk(
    "doctorAvailability/update",

    async (
      payload,
      { rejectWithValue }
    ) => {
      try {
        if (!payload) {
          return rejectWithValue(
            "Availability data is required"
          );
        }

        const res =
          await api.put(
            "/my",
            payload
          );

        return res.data;
      } catch (err) {
        return rejectWithValue(
          getErrorMessage(
            err,
            "Failed to update availability"
          )
        );
      }
    }
  );

// ============================================================
// ✏️ UPDATE SINGLE SLOT
// PUT /slot
// ============================================================

export const updateSingleSlot =
  createAsyncThunk(
    "doctorAvailability/updateSingleSlot",

    async (
      payload,
      { rejectWithValue }
    ) => {
      try {
        if (!payload) {
          return rejectWithValue(
            "Slot data is required"
          );
        }

        const res =
          await api.put(
            "/slot",
            payload
          );

        return res.data;
      } catch (err) {
        return rejectWithValue(
          getErrorMessage(
            err,
            "Failed to update slot"
          )
        );
      }
    }
  );

// ============================================================
// ✏️ UPDATE DOCTOR ROUND
// PUT /round
// ============================================================

export const updateRound =
  createAsyncThunk(
    "doctorAvailability/updateRound",

    async (
      payload,
      { rejectWithValue }
    ) => {
      try {
        if (!payload?.date) {
          return rejectWithValue(
            "Date is required"
          );
        }

        const roundId =
          payload?.roundId ||
          payload?._id ||
          payload?.id;

        if (!roundId) {
          return rejectWithValue(
            "Round ID is required"
          );
        }

        if (
          !payload?.name &&
          !payload?.roundName
        ) {
          return rejectWithValue(
            "Round name is required"
          );
        }

        if (!payload?.startTime) {
          return rejectWithValue(
            "Round start time is required"
          );
        }

        if (!payload?.endTime) {
          return rejectWithValue(
            "Round end time is required"
          );
        }

        const res =
          await api.put(
            "/round",
            {
              ...payload,
              roundId,
            }
          );

        return res.data;
      } catch (err) {
        return rejectWithValue(
          getErrorMessage(
            err,
            "Failed to update doctor round"
          )
        );
      }
    }
  );

// ============================================================
// ✏️ UPDATE OPERATION
// PUT /operation
// ============================================================

export const updateOperation =
  createAsyncThunk(
    "doctorAvailability/updateOperation",

    async (
      payload,
      { rejectWithValue }
    ) => {
      try {
        if (!payload?.date) {
          return rejectWithValue(
            "Date is required"
          );
        }

        const operationId =
          payload?.operationId ||
          payload?._id ||
          payload?.id;

        if (!operationId) {
          return rejectWithValue(
            "Operation ID is required"
          );
        }

        if (
          !payload?.name &&
          !payload?.operationName &&
          !payload?.procedureName
        ) {
          return rejectWithValue(
            "Operation name is required"
          );
        }

        if (!payload?.startTime) {
          return rejectWithValue(
            "Operation start time is required"
          );
        }

        if (!payload?.endTime) {
          return rejectWithValue(
            "Operation end time is required"
          );
        }

        const res =
          await api.put(
            "/operation",
            {
              ...payload,
              operationId,
            }
          );

        return res.data;
      } catch (err) {
        return rejectWithValue(
          getErrorMessage(
            err,
            "Failed to update operation schedule"
          )
        );
      }
    }
  );

// ============================================================
// ❌ DELETE SINGLE SLOT
// DELETE /slot
// ============================================================

export const deleteSingleSlot =
  createAsyncThunk(
    "doctorAvailability/deleteSingleSlot",

    async (
      payload,
      { rejectWithValue }
    ) => {
      try {
        if (!payload) {
          return rejectWithValue(
            "Slot data is required"
          );
        }

        const res =
          await api.delete(
            "/slot",
            {
              data: payload,
            }
          );

        return res.data;
      } catch (err) {
        return rejectWithValue(
          getErrorMessage(
            err,
            "Failed to delete slot"
          )
        );
      }
    }
  );

// ============================================================
// ❌ DELETE ENTIRE DAY
// DELETE /day
// ============================================================

export const deleteEntireDay =
  createAsyncThunk(
    "doctorAvailability/deleteEntireDay",

    async (
      { date },
      { rejectWithValue }
    ) => {
      try {
        if (!date) {
          return rejectWithValue(
            "Date is required"
          );
        }

        const res =
          await api.delete(
            "/day",
            {
              data: {
                date,
              },
            }
          );

        return res.data;
      } catch (err) {
        return rejectWithValue(
          getErrorMessage(
            err,
            "Failed to delete day"
          )
        );
      }
    }
  );

// ============================================================
// ❌ DELETE SESSION
// DELETE /session
// ============================================================

export const deleteSession =
  createAsyncThunk(
    "doctorAvailability/deleteSession",

    async (
      payload,
      { rejectWithValue }
    ) => {
      try {
        if (!payload?.date) {
          return rejectWithValue(
            "Date is required"
          );
        }

        if (!payload?.sessionName) {
          return rejectWithValue(
            "Session name is required"
          );
        }

        const res =
          await api.delete(
            "/session",
            {
              data: payload,
            }
          );

        return res.data;
      } catch (err) {
        return rejectWithValue(
          getErrorMessage(
            err,
            "Failed to delete session"
          )
        );
      }
    }
  );

// ============================================================
// ❌ DELETE DOCTOR ROUND
// DELETE /round
// ============================================================

// ============================================================
// ❌ DELETE DOCTOR ROUND
// DELETE /round/delete
// ============================================================

export const deleteRound = createAsyncThunk(
  "doctorAvailability/deleteRound",

  async (payload, { rejectWithValue }) => {
    try {
      console.log("🔥 deleteRound THUNK CALLED");
      console.log("🔥 deleteRound PAYLOAD:", payload);

      if (!payload?.date) {
        console.log("❌ DATE MISSING");
        return rejectWithValue("Date is required");
      }

      const roundId =
        payload?.roundId ||
        payload?._id ||
        payload?.id;

      console.log("🔥 ROUND ID:", roundId);

      if (!roundId) {
        console.log("❌ ROUND ID MISSING");
        return rejectWithValue("Round ID is required");
      }

      console.log(
        "🔥 ABOUT TO SEND:",
        "/round/delete"
      );

      const res = await api.delete(
        "/round/delete",
        {
          data: {
            ...payload,
            roundId,
          },
        }
      );

      console.log("✅ DELETE RESPONSE:", res.data);

      return res.data;
    } catch (err) {
      console.error("❌ DELETE ROUND ERROR:", err);

      return rejectWithValue(
        getErrorMessage(
          err,
          "Failed to delete doctor round"
        )
      );
    }
  }
);
// ============================================================
// ❌ DELETE OPERATION
// DELETE /operation/delete
// ============================================================

export const deleteOperation = createAsyncThunk(
  "doctorAvailability/deleteOperation",

  async (payload, { rejectWithValue }) => {
    try {
      console.log(
        "🔥 DELETE OPERATION PAYLOAD:",
        payload
      );

      if (!payload?.date) {
        return rejectWithValue(
          "Date is required"
        );
      }

      const operationId =
        payload?.operationId ||
        payload?._id ||
        payload?.id;

      if (!operationId) {
        return rejectWithValue(
          "Operation ID is required"
        );
      }

      const requestData = {
        date: payload.date,
        operationId,
      };

      if (payload?.doctorId) {
        requestData.doctorId =
          payload.doctorId;
      }

      console.log(
        "🚀 DELETE OPERATION REQUEST:",
        requestData
      );

      const response = await api.delete(
        "/operation/delete",
        {
          data: requestData,
        }
      );

      console.log(
        "✅ DELETE OPERATION RESPONSE:",
        response.data
      );

      return response.data;

    } catch (err) {
      console.error(
        "❌ DELETE OPERATION ERROR:",
        err
      );

      return rejectWithValue(
        getErrorMessage(
          err,
          "Failed to delete operation"
        )
      );
    }
  }
);
// ============================================================
// 📅 GET AVAILABILITY BY DATE
// GET /by-date
// ============================================================

export const getAvailabilityByDate =
  createAsyncThunk(
    "doctorAvailability/getByDate",

    async (
      {
        doctorId,
        date,
      },
      { rejectWithValue }
    ) => {
      try {
        if (!doctorId) {
          return rejectWithValue(
            "doctorId is required"
          );
        }

        if (!date) {
          return rejectWithValue(
            "date is required"
          );
        }

        const res =
          await api.get(
            "/by-date",
            {
              params: {
                doctorId,
                date,
              },
            }
          );

        return normalizeAvailabilityData(
          res.data || {}
        );
      } catch (err) {
        return rejectWithValue(
          getErrorMessage(
            err,
            "Failed to fetch availability"
          )
        );
      }
    }
  );

// ============================================================
// 🌍 GET ALL SLOTS BY DOCTOR
// GET /all-slots
// ============================================================

export const getAllSlotsByDoctor =
  createAsyncThunk(
    "doctorAvailability/getAllSlots",

    async (
      doctorId,
      { rejectWithValue }
    ) => {
      try {
        if (!doctorId) {
          return rejectWithValue(
            "doctorId is required"
          );
        }

        const res =
          await api.get(
            "/all-slots",
            {
              params: {
                doctorId,
              },
            }
          );

        const slots =
          Array.isArray(
            res.data?.slots
          )
            ? res.data.slots
            : [];

        const counts =
          calculateSlotCounts(
            slots
          );

        return {
          success:
            res.data?.success ??
            true,

          slots,

          totalSlots:
            res.data?.totalSlots ??
            counts.total,

          bookedSlots:
            res.data?.bookedSlots ??
            counts.booked,

          availableSlots:
            res.data?.availableSlots ??
            counts.available,

          blockedSlots:
            res.data?.blockedSlots ??
            counts.blocked,
        };
      } catch (err) {
        return rejectWithValue(
          getErrorMessage(
            err,
            "Failed to fetch all slots"
          )
        );
      }
    }
  );

// ============================================================
// 👨‍⚕️ GET MY AVAILABILITY
// GET /my
// ============================================================

export const getMyAvailability =
  createAsyncThunk(
    "doctorAvailability/getMy",

    async (
      _,
      { rejectWithValue }
    ) => {
      try {
        const res =
          await api.get(
            "/my"
          );

        const response =
          res.data || {};

        const rawData =
          response.data &&
          typeof response.data ===
            "object"
            ? response.data
            : null;

        if (!rawData) {
          return {
            success:
              response.success ??
              true,

            data: null,
          };
        }

        const normalized =
          normalizeAvailabilityData(
            rawData
          );

        return {
          success:
            response.success ??
            true,

          data: {
            ...rawData,

            ...normalized,

            weeklyAvailability:
              Array.isArray(
                rawData.weeklyAvailability
              )
                ? rawData.weeklyAvailability
                : [],

            customDates:
              Array.isArray(
                rawData.customDates
              )
                ? rawData.customDates
                : [],
          },
        };
      } catch (err) {
        return rejectWithValue(
          getErrorMessage(
            err,
            "Failed to fetch availability"
          )
        );
      }
    }
  );

// ============================================================
// 🧠 INITIAL STATE
// ============================================================

const initialState = {
  selectedDate: null,

  source: "",

  isPastDay: false,

  isToday: false,

  isHoliday: false,

  holidayReason: "",

  sessions: [],

  allSlots: [],

  doctorOnRounds: [],

  operationSchedule: [],

  operationSchedules: [],

  totalSlots: 0,

  bookedSlots: 0,

  availableSlots: 0,

  blockedSlots: 0,

  myAvailability: null,

  loading: false,

  error: null,

  success: null,
};

// ============================================================
// 🧠 SLICE
// ============================================================

const doctorAvailabilitySlice =
  createSlice({
    name: "doctorAvailability",

    initialState,

    reducers: {
      // ======================================================
      // CLEAR EVERYTHING
      // ======================================================

      clearState: (state) => {
        resetDateState(state);

        state.loading = false;
        state.error = null;
        state.success = null;
      },

      // ======================================================
      // CLEAR MESSAGES
      // ======================================================

      clearMessages: (state) => {
        state.error = null;
        state.success = null;
      },

      // ======================================================
      // CLEAR DATE
      // ======================================================

      clearDateAvailability: (
        state
      ) => {
        resetDateState(state);
      },
    },

    extraReducers: (builder) => {
      builder

        // ====================================================
        // 📅 GET BY DATE
        // ====================================================

        .addCase(
          getAvailabilityByDate.pending,
          (state) => {
            state.loading = true;
            state.error = null;
          }
        )

        .addCase(
          getAvailabilityByDate.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;

            const data =
              action.payload || {};

            state.selectedDate =
              data.date ||
              state.selectedDate;

            state.source =
              data.source || "";

            state.isPastDay =
              data.isPastDay ??
              false;

            state.isToday =
              data.isToday ??
              false;

            state.isHoliday =
              data.isHoliday ??
              false;

            state.holidayReason =
              data.holidayReason ||
              "";

            state.sessions =
              Array.isArray(
                data.sessions
              )
                ? data.sessions
                : [];

            state.allSlots =
              Array.isArray(
                data.allSlots
              )
                ? data.allSlots
                : [];

            state.doctorOnRounds =
              Array.isArray(
                data.doctorOnRounds
              )
                ? data.doctorOnRounds
                : Array.isArray(
                    data.rounds
                  )
                ? data.rounds
                : [];

            const operations =
              Array.isArray(
                data.operationSchedules
              )
                ? data.operationSchedules
                : Array.isArray(
                    data.operationSchedule
                  )
                ? data.operationSchedule
                : [];

            state.operationSchedule =
              operations;

            state.operationSchedules =
              operations;

            state.totalSlots =
              toNumber(
                data.totalSlots
              );

            state.bookedSlots =
              toNumber(
                data.bookedSlots
              );

            state.availableSlots =
              toNumber(
                data.availableSlots
              );

            state.blockedSlots =
              toNumber(
                data.blockedSlots
              );
          }
        )

        // ====================================================
        // 🌍 GET ALL SLOTS
        // ====================================================

        .addCase(
          getAllSlotsByDoctor.pending,
          (state) => {
            state.loading = true;
            state.error = null;
          }
        )

        .addCase(
          getAllSlotsByDoctor.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;

            const data =
              action.payload || {};

            state.allSlots =
              Array.isArray(
                data.slots
              )
                ? data.slots
                : [];

            state.totalSlots =
              toNumber(
                data.totalSlots
              );

            state.bookedSlots =
              toNumber(
                data.bookedSlots
              );

            state.availableSlots =
              toNumber(
                data.availableSlots
              );

            state.blockedSlots =
              toNumber(
                data.blockedSlots
              );
          }
        )

        // ====================================================
        // 👨‍⚕️ GET MY AVAILABILITY
        // ====================================================

        .addCase(
          getMyAvailability.pending,
          (state) => {
            state.loading = true;
            state.error = null;
          }
        )

        .addCase(
          getMyAvailability.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;

            const data =
              action.payload?.data ||
              null;

            state.myAvailability =
              data;

            if (!data) {
              return;
            }

            state.selectedDate =
              data.date ||
              state.selectedDate;

            state.source =
              data.source ||
              state.source;

            state.isPastDay =
              data.isPastDay ??
              false;

            state.isToday =
              data.isToday ??
              false;

            state.isHoliday =
              data.isHoliday ??
              false;

            state.holidayReason =
              data.holidayReason ||
              "";

            state.sessions =
              Array.isArray(
                data.sessions
              )
                ? data.sessions
                : [];

            state.allSlots =
              Array.isArray(
                data.allSlots
              )
                ? data.allSlots
                : [];

            state.doctorOnRounds =
              Array.isArray(
                data.doctorOnRounds
              )
                ? data.doctorOnRounds
                : [];

            const operations =
              Array.isArray(
                data.operationSchedules
              )
                ? data.operationSchedules
                : Array.isArray(
                    data.operationSchedule
                  )
                ? data.operationSchedule
                : [];

            state.operationSchedule =
              operations;

            state.operationSchedules =
              operations;

            state.totalSlots =
              toNumber(
                data.totalSlots
              );

            state.bookedSlots =
              toNumber(
                data.bookedSlots
              );

            state.availableSlots =
              toNumber(
                data.availableSlots
              );

            state.blockedSlots =
              toNumber(
                data.blockedSlots
              );
          }
        )

        // ====================================================
        // 📦 CREATE
        // ====================================================

        .addCase(
          createAvailability.pending,
          (state) => {
            state.loading = true;
            state.error = null;
            state.success = null;
          }
        )

        .addCase(
          createAvailability.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;

            state.success =
              action.payload
                ?.message ||
              "Availability created successfully";

            const data =
              normalizeAvailabilityData(
                action.payload
              );

            state.myAvailability =
              data;

            state.selectedDate =
              data.date ||
              state.selectedDate;

            state.source =
              data.source || "";

            state.isPastDay =
              data.isPastDay ??
              false;

            state.isToday =
              data.isToday ??
              false;

            state.isHoliday =
              data.isHoliday ??
              false;

            state.holidayReason =
              data.holidayReason ||
              "";

            state.sessions =
              data.sessions;

            state.allSlots =
              data.allSlots;

            state.doctorOnRounds =
              data.doctorOnRounds;

            state.operationSchedule =
              data.operationSchedule;

            state.operationSchedules =
              data.operationSchedules;

            state.totalSlots =
              data.totalSlots;

            state.bookedSlots =
              data.bookedSlots;

            state.availableSlots =
              data.availableSlots;

            state.blockedSlots =
              data.blockedSlots;
          }
        )

        // ====================================================
        // ➕ ADD DAY
        // ====================================================

        .addCase(
          addEntireDay.pending,
          (state) => {
            state.loading = true;
            state.error = null;
            state.success = null;
          }
        )

        .addCase(
          addEntireDay.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;

            state.success =
              action.payload
                ?.message ||
              "Day added successfully";

            const data =
              normalizeAvailabilityData(
                action.payload
              );

            state.selectedDate =
              data.date ||
              state.selectedDate;

            state.source =
              data.source ||
              state.source;

            state.isPastDay =
              data.isPastDay ??
              false;

            state.isToday =
              data.isToday ??
              false;

            state.isHoliday =
              data.isHoliday ??
              false;

            state.holidayReason =
              data.holidayReason ||
              "";

            state.sessions =
              data.sessions;

            state.allSlots =
              data.allSlots;

            state.doctorOnRounds =
              data.doctorOnRounds;

            state.operationSchedule =
              data.operationSchedule;

            state.operationSchedules =
              data.operationSchedules;

            state.totalSlots =
              data.totalSlots;

            state.bookedSlots =
              data.bookedSlots;

            state.availableSlots =
              data.availableSlots;

            state.blockedSlots =
              data.blockedSlots;

            state.myAvailability =
              data;
          }
        )

        // ====================================================
        // ➕ ADD SLOT
        // ====================================================

        .addCase(
          addSingleSlot.pending,
          (state) => {
            state.loading = true;
            state.error = null;
            state.success = null;
          }
        )

        .addCase(
          addSingleSlot.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;

            state.success =
              action.payload
                ?.message ||
              "Slot added successfully";

            const data =
              normalizeAvailabilityData(
                action.payload
              );

            if (
              data.sessions.length >
              0
            ) {
              state.sessions =
                data.sessions;

              state.allSlots =
                data.allSlots;

              state.totalSlots =
                data.totalSlots;

              state.bookedSlots =
                data.bookedSlots;

              state.availableSlots =
                data.availableSlots;

              state.blockedSlots =
                data.blockedSlots;
            }

            state.doctorOnRounds =
              data.doctorOnRounds;

            state.operationSchedule =
              data.operationSchedule;

            state.operationSchedules =
              data.operationSchedules;

            if (data.date) {
              state.selectedDate =
                data.date;
            }
          }
        )

        // ====================================================
        // 📋 COPY WEEKLY
        // ====================================================

        .addCase(
          copyWeeklyAvailability.pending,
          (state) => {
            state.loading = true;
            state.error = null;
            state.success = null;
          }
        )

        .addCase(
          copyWeeklyAvailability.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;

            state.success =
              action.payload
                ?.message ||
              "Weekly availability copied successfully";

            const data =
              normalizeAvailabilityData(
                action.payload
              );

            state.selectedDate =
              data.date ||
              state.selectedDate;

            state.source =
              data.source ||
              state.source;

            state.isPastDay =
              data.isPastDay ??
              false;

            state.isToday =
              data.isToday ??
              false;

            state.isHoliday =
              data.isHoliday ??
              false;

            state.holidayReason =
              data.holidayReason ||
              "";

            state.sessions =
              data.sessions;

            state.allSlots =
              data.allSlots;

            state.doctorOnRounds =
              data.doctorOnRounds;

            state.operationSchedule =
              data.operationSchedule;

            state.operationSchedules =
              data.operationSchedules;

            state.totalSlots =
              data.totalSlots;

            state.bookedSlots =
              data.bookedSlots;

            state.availableSlots =
              data.availableSlots;

            state.blockedSlots =
              data.blockedSlots;

            state.myAvailability =
              data;
          }
        )

        // ====================================================
        // ✏️ UPDATE FULL
        // ====================================================

        .addCase(
          updateAvailability.pending,
          (state) => {
            state.loading = true;
            state.error = null;
            state.success = null;
          }
        )

        .addCase(
          updateAvailability.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;

            state.success =
              action.payload
                ?.message ||
              "Availability updated successfully";

            const data =
              normalizeAvailabilityData(
                action.payload
              );

            state.myAvailability =
              data;

            state.selectedDate =
              data.date ||
              state.selectedDate;

            state.source =
              data.source ||
              state.source;

            state.isPastDay =
              data.isPastDay ??
              false;

            state.isToday =
              data.isToday ??
              false;

            state.isHoliday =
              data.isHoliday ??
              false;

            state.holidayReason =
              data.holidayReason ||
              "";

            state.sessions =
              data.sessions;

            state.allSlots =
              data.allSlots;

            state.doctorOnRounds =
              data.doctorOnRounds;

            state.operationSchedule =
              data.operationSchedule;

            state.operationSchedules =
              data.operationSchedules;

            state.totalSlots =
              data.totalSlots;

            state.bookedSlots =
              data.bookedSlots;

            state.availableSlots =
              data.availableSlots;

            state.blockedSlots =
              data.blockedSlots;
          }
        )

        // ====================================================
        // ✏️ UPDATE SINGLE SLOT
        // ====================================================

        .addCase(
          updateSingleSlot.pending,
          (state) => {
            state.loading = true;
            state.error = null;
            state.success = null;
          }
        )

        .addCase(
          updateSingleSlot.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;

            state.success =
              action.payload
                ?.message ||
              "Slot updated successfully";

            const updatedSlot =
              action.payload?.slot ||
              action.payload?.data;

            if (!updatedSlot) {
              return;
            }

            const updatedId =
              getId(
                updatedSlot
              );

            if (!updatedId) {
              return;
            }

            state.allSlots =
              state.allSlots.map(
                (slot) =>
                  sameId(
                    getId(slot),
                    updatedId
                  )
                    ? {
                        ...slot,
                        ...updatedSlot,
                      }
                    : slot
              );

            state.sessions =
              state.sessions.map(
                (session) => ({
                  ...session,

                  slots:
                    Array.isArray(
                      session.slots
                    )
                      ? session.slots.map(
                          (slot) =>
                            sameId(
                              getId(slot),
                              updatedId
                            )
                              ? {
                                  ...slot,
                                  ...updatedSlot,
                                }
                              : slot
                        )
                      : [],
                })
              );

            recalculateStateCounts(
              state
            );
          }
        )

        // ====================================================
        // ✏️ UPDATE DOCTOR ROUND
        // ====================================================

        .addCase(
          updateRound.pending,
          (state) => {
            state.loading = true;
            state.error = null;
            state.success = null;
          }
        )

        .addCase(
          updateRound.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;

            state.success =
              action.payload
                ?.message ||
              "Doctor round updated successfully";

            const payload =
              action.payload || {};

            const updatedRound =
              payload.round ||
              payload.doctorRound ||
              payload.data;

            const requestedId =
              action.meta?.arg
                ?.roundId ||
              action.meta?.arg
                ?._id ||
              action.meta?.arg?.id;

            const updatedId =
              getRoundId(
                updatedRound
              ) ||
              requestedId;

            if (!updatedId) {
              return;
            }

            state.doctorOnRounds =
              state.doctorOnRounds.map(
                (round) =>
                  sameId(
                    getRoundId(round),
                    updatedId
                  )
                    ? {
                        ...round,
                        ...(updatedRound ||
                          action.meta
                            ?.arg),
                      }
                    : round
              );
          }
        )

        // ====================================================
        // ✏️ UPDATE OPERATION
        // ====================================================

        .addCase(
          updateOperation.pending,
          (state) => {
            state.loading = true;
            state.error = null;
            state.success = null;
          }
        )

        .addCase(
          updateOperation.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;

            state.success =
              action.payload
                ?.message ||
              "Operation schedule updated successfully";

            const payload =
              action.payload || {};

            const updatedOperation =
              payload.operation ||
              payload.operationSchedule ||
              payload.data;

            const requestedId =
              action.meta?.arg
                ?.operationId ||
              action.meta?.arg
                ?._id ||
              action.meta?.arg?.id;

            const updatedId =
              getOperationId(
                updatedOperation
              ) ||
              requestedId;

            if (!updatedId) {
              return;
            }

            const updatedOperations =
              state.operationSchedule.map(
                (operation) =>
                  sameId(
                    getOperationId(
                      operation
                    ),
                    updatedId
                  )
                    ? {
                        ...operation,
                        ...(updatedOperation ||
                          action.meta
                            ?.arg),
                      }
                    : operation
              );

            state.operationSchedule =
              updatedOperations;

            state.operationSchedules =
              updatedOperations;
          }
        )

        // ====================================================
        // ❌ DELETE SINGLE SLOT
        // ====================================================

        .addCase(
          deleteSingleSlot.pending,
          (state) => {
            state.loading = true;
            state.error = null;
            state.success = null;
          }
        )

        .addCase(
          deleteSingleSlot.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;

            state.success =
              action.payload
                ?.message ||
              "Slot deleted successfully";

            const deletedSlotId =
              action.payload
                ?.slotId ||
              action.payload
                ?.deletedSlotId ||
              action.payload
                ?.data?.slotId;

            const requestedId =
              action.meta?.arg
                ?.slotId ||
              action.meta?.arg
                ?._id ||
              action.meta?.arg?.id;

            const finalDeletedId =
              deletedSlotId ||
              requestedId;

            if (!finalDeletedId) {
              return;
            }

            state.allSlots =
              state.allSlots.filter(
                (slot) =>
                  !sameId(
                    getId(slot),
                    finalDeletedId
                  )
              );

            state.sessions =
              state.sessions.map(
                (session) => ({
                  ...session,

                  slots:
                    Array.isArray(
                      session.slots
                    )
                      ? session.slots.filter(
                          (slot) =>
                            !sameId(
                              getId(slot),
                              finalDeletedId
                            )
                        )
                      : [],
                })
              );

            recalculateStateCounts(
              state
            );
          }
        )

        // ====================================================
        // ❌ DELETE SESSION
        // ====================================================

        .addCase(
          deleteSession.pending,
          (state) => {
            state.loading = true;
            state.error = null;
            state.success = null;
          }
        )

        .addCase(
          deleteSession.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;

            state.success =
              action.payload
                ?.message ||
              "Session deleted successfully";

            const sessionName =
              action.meta
                ?.arg?.sessionName;

            if (!sessionName) {
              return;
            }

            const normalizedName =
              String(
                sessionName
              )
                .trim()
                .toLowerCase();

            const removedSession =
              state.sessions.find(
                (session) =>
                  String(
                    session?.name ||
                      session?.sessionName ||
                      ""
                  )
                    .trim()
                    .toLowerCase() ===
                  normalizedName
              );

            const removedIds =
              Array.isArray(
                removedSession?.slots
              )
                ? removedSession.slots.map(
                    getId
                  )
                : [];

            state.sessions =
              state.sessions.filter(
                (session) =>
                  String(
                    session?.name ||
                      session?.sessionName ||
                      ""
                  )
                    .trim()
                    .toLowerCase() !==
                  normalizedName
              );

            if (
              removedIds.length > 0
            ) {
              state.allSlots =
                state.allSlots.filter(
                  (slot) =>
                    !removedIds.some(
                      (id) =>
                        sameId(
                          getId(slot),
                          id
                        )
                    )
                );
            }

            recalculateStateCounts(
              state
            );
          }
        )

        // ====================================================
        // ❌ DELETE ROUND
        // ====================================================

        .addCase(
          deleteRound.pending,
          (state) => {
            state.loading = true;
            state.error = null;
            state.success = null;
          }
        )

        .addCase(
          deleteRound.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;

            state.success =
              action.payload
                ?.message ||
              "Doctor round deleted successfully";

            const requestedId =
              action.meta
                ?.arg?.roundId ||
              action.meta
                ?.arg?._id ||
              action.meta
                ?.arg?.id;

            const returnedId =
              action.payload
                ?.roundId ||
              action.payload
                ?.deletedRoundId ||
              action.payload
                ?.data?.roundId;

            const deletedId =
              returnedId ||
              requestedId;

            if (!deletedId) {
              return;
            }

            state.doctorOnRounds =
              state.doctorOnRounds.filter(
                (round) =>
                  !sameId(
                    getRoundId(round),
                    deletedId
                  )
              );
          }
        )

        // ====================================================
        // ❌ DELETE OPERATION
        // ====================================================

        .addCase(
          deleteOperation.pending,
          (state) => {
            state.loading = true;
            state.error = null;
            state.success = null;
          }
        )

        .addCase(
          deleteOperation.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;

            state.success =
              action.payload
                ?.message ||
              "Operation deleted successfully";

            const requestedId =
              action.meta
                ?.arg?.operationId ||
              action.meta
                ?.arg?._id ||
              action.meta
                ?.arg?.id;

            const returnedId =
              action.payload
                ?.operationId ||
              action.payload
                ?.deletedOperationId ||
              action.payload
                ?.data?.operationId;

            const deletedId =
              returnedId ||
              requestedId;

            if (!deletedId) {
              return;
            }

            const filtered =
              state.operationSchedule.filter(
                (operation) =>
                  !sameId(
                    getOperationId(
                      operation
                    ),
                    deletedId
                  )
              );

            state.operationSchedule =
              filtered;

            state.operationSchedules =
              filtered;
          }
        )

        // ====================================================
        // ❌ DELETE ENTIRE DAY
        // ====================================================

        .addCase(
          deleteEntireDay.pending,
          (state) => {
            state.loading = true;
            state.error = null;
            state.success = null;
          }
        )

        .addCase(
          deleteEntireDay.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;

            resetDateState(
              state
            );

            state.success =
              action.payload
                ?.message ||
              "Day deleted successfully";
          }
        )

        // ====================================================
        // ❌ GLOBAL REJECTED
        // ====================================================

        .addMatcher(
          (action) =>
            action.type.endsWith(
              "/rejected"
            ),

          (
            state,
            action
          ) => {
            state.loading = false;

            state.error =
              action.payload ||
              action.error?.message ||
              "Something went wrong";
          }
        );
    },
  });

// ============================================================
// ACTIONS
// ============================================================

export const {
  clearState,
  clearMessages,
  clearDateAvailability,
} =
  doctorAvailabilitySlice.actions;

// ============================================================
// SELECTORS
// ============================================================

export const selectAvailability =
  (state) =>
    state.doctorAvailability;

export const selectSessions =
  (state) =>
    state.doctorAvailability
      ?.sessions || [];

export const selectAllSlots =
  (state) =>
    state.doctorAvailability
      ?.allSlots || [];

export const selectDoctorRounds =
  (state) =>
    state.doctorAvailability
      ?.doctorOnRounds || [];

export const selectOperationSchedule =
  (state) =>
    state.doctorAvailability
      ?.operationSchedule || [];

export const selectOperationSchedules =
  (state) =>
    state.doctorAvailability
      ?.operationSchedules ||
    state.doctorAvailability
      ?.operationSchedule ||
    [];

export const selectIsHoliday =
  (state) =>
    state.doctorAvailability
      ?.isHoliday || false;

export const selectHolidayReason =
  (state) =>
    state.doctorAvailability
      ?.holidayReason || "";

export const selectIsPastDay =
  (state) =>
    state.doctorAvailability
      ?.isPastDay || false;

export const selectIsToday =
  (state) =>
    state.doctorAvailability
      ?.isToday || false;

export const selectAvailabilityLoading =
  (state) =>
    state.doctorAvailability
      ?.loading || false;

export const selectAvailabilityError =
  (state) =>
    state.doctorAvailability
      ?.error || null;

export const selectAvailabilitySuccess =
  (state) =>
    state.doctorAvailability
      ?.success || null;

export const selectTotalSlots =
  (state) =>
    state.doctorAvailability
      ?.totalSlots || 0;

export const selectBookedSlots =
  (state) =>
    state.doctorAvailability
      ?.bookedSlots || 0;

export const selectAvailableSlots =
  (state) =>
    state.doctorAvailability
      ?.availableSlots || 0;

export const selectBlockedSlots =
  (state) =>
    state.doctorAvailability
      ?.blockedSlots || 0;

// ============================================================
// 📊 SLOT DETAILS
// ============================================================

export const selectSlotDetails =
  (state) => {
    const availability =
      state.doctorAvailability ||
      {};

    const sessions =
      Array.isArray(
        availability.sessions
      )
        ? availability.sessions
        : [];

    const getSession =
      (names) => {
        const normalizedNames =
          names.map((name) =>
            String(name)
              .trim()
              .toLowerCase()
          );

        const found =
          sessions.find(
            (session) =>
              normalizedNames.includes(
                String(
                  session?.name ||
                    session?.sessionName ||
                    ""
                )
                  .trim()
                  .toLowerCase()
              )
          );

        if (!found) {
          return {
            total: 0,
            booked: 0,
            available: 0,
            blocked: 0,
          };
        }

        return {
          total:
            toNumber(
              found.total
            ),

          booked:
            toNumber(
              found.booked
            ),

          available:
            toNumber(
              found.available
            ),

          blocked:
            toNumber(
              found.blocked
            ),
        };
      };

    return {
      totalSlots:
        toNumber(
          availability.totalSlots
        ),

      bookedSlots:
        toNumber(
          availability.bookedSlots
        ),

      availableSlots:
        toNumber(
          availability.availableSlots
        ),

      blockedSlots:
        toNumber(
          availability.blockedSlots
        ),

      morning:
        getSession([
          "morning",
        ]),

      afternoon:
        getSession([
          "afternoon",
        ]),

      evening:
        getSession([
          "evening",
        ]),

      night:
        getSession([
          "night",
        ]),

      slots:
        Array.isArray(
          availability.allSlots
        )
          ? availability.allSlots
          : [],
    };
  };

// ============================================================
// 📊 SLOT UTILIZATION
// ============================================================

export const selectSlotUtilization =
  (state) => {
    const total =
      toNumber(
        state.doctorAvailability
          ?.totalSlots
      );

    const booked =
      toNumber(
        state.doctorAvailability
          ?.bookedSlots
      );

    if (!total) {
      return 0;
    }

    return Math.round(
      (booked / total) * 100
    );
  };

// ============================================================
// REDUCER
// ============================================================

export default doctorAvailabilitySlice.reducer;
